import * as _ from 'underscore';

import {
  IAllowedActionScope,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  IDisplayWorkbookTabsScope,
  IGetUserUrlScope,
  RefreshContentScope,
} from '../../app/ActionPlaceScopes';
import { appStore } from '../../app/AppStore';
import { ContentPlaceHeaderProps } from '../../app/content/placePages/PlaceHeader';
import { IPlaceScope } from '../../app/IPlaceScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import {
  DetailsDialogInAngularPlaceHelper,
  IDetailsDialogInAngularPlaceHelper
} from '../../app/spaces/DetailsDialog/AngularContentDetailsDialog';
import { WorkbookSpaceOwnProps } from '../../app/spaces/WorkbookSpace/WorkbookSpace';
import { Location } from '../../common/react/Location';
import { ActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../helpers/ResultHelper';
import { IUserInfo } from '../models/IUserInfo';
import { IWorkbookDetailInfo, IWorkbookInfo } from '../models/IWorkbookInfo';
import { IItemStatisticsInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IWorkbookUsageInfo } from '../ServerApi/types/IUsageInfo';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { ChangeWorkbookDescriptionAction } from '../services/ChangeWorkbookDescriptionAction';
import { ContentActions } from '../services/ContentActions';
import { RevisionHistoryAction } from '../services/RevisionHistoryAction';
import { ToasterService } from '../services/ToasterService';


export interface IWorkbookDetailsDialogScope extends
  IAllowedActionScope<IWorkbookActions>,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  IPlaceScope,
  IDisplayWorkbookTabsScope,
  RefreshContentScope,
  IGetUserUrlScope
{
  moveToProject: ($event: JQueryEventObject) => void;
  workbook: IWorkbookDetailInfo & IItemStatisticsInfo<IWorkbookUsageInfo>;
  workbookSet: Set<IWorkbookDetailInfo>;
  workbookPlaceHeaderProps: ContentPlaceHeaderProps;
  showRevisionHistory: ($event: JQueryEventObject) => any;
  workbookSpaceProps: WorkbookSpaceOwnProps;
  getProjectUrl: (projectId: string) => string;
}

export class WorkbookDetailsDialogCtrl {

  public static $inject = [
    '$q',
    '$scope',
    'ChangeWorkbookDescriptionAction',
    'ContentActions',
    'RevisionHistoryAction',
    'workbookActions',
    'workbookDetailInfo',
    'workbookInfo',
    'detailsDialogHelper'
  ];

  constructor(
    $q: ng.IQService,
    $scope: IWorkbookDetailsDialogScope,
    private ChangeWorkbookDescriptionAction: ChangeWorkbookDescriptionAction,
    private ContentActions: ContentActions,
    private RevisionHistoryAction: RevisionHistoryAction,
    private workbookActions: IWorkbookActions,
    workbookDetailInfo: IWorkbookDetailInfo,
    workbookInfo: IWorkbookInfo,
    detailsDialogHelper: DetailsDialogInAngularPlaceHelper
  ) {
    const allowedActionFn = (actionName: keyof IWorkbookActions) => (!!this.workbookActions && this.workbookActions[actionName]);
    const refreshContentFn = () => window.location.reload();

    this.loadWorkbookIntoScope($scope, workbookInfo, workbookDetailInfo);
    this.initScopeForDetailsPane($scope, $q, allowedActionFn, refreshContentFn, detailsDialogHelper, ToasterService.instance);
  }

  private loadWorkbookIntoScope(
    $scope: IWorkbookDetailsDialogScope,
    workbookInfo: IWorkbookInfo,
    workbookDetailInfo: IWorkbookDetailInfo) {

    $scope.workbook = {
      usageInfo: workbookInfo.usageInfo,
      usageInfoMaxValues: workbookInfo.usageInfoMaxValues,
      ...workbookDetailInfo
    };

    $scope.workbookSet = Set.singletonForIdItem(workbookDetailInfo);

    $scope.objectName = workbookDetailInfo.name;
  }

  private initScopeForDetailsPane(
    $scope: IWorkbookDetailsDialogScope,
    $q: ng.IQService,
    allowedActionFn: (actionName: string) => boolean,
    refreshContentFn: () => void,
    dialogHelper: IDetailsDialogInAngularPlaceHelper,
    toaster: ToasterService
  ) {

    // $scope.refreshContent is used by some of the menu actions too; be very careful about deleting it
    $scope.refreshContent = refreshContentFn;
    $scope.allowedAction = allowedActionFn;

    $scope.del = ($event: JQueryEventObject) => {
      this.ContentActions.deleteWorkbook($scope.workbook, ActionContext.fromEvent($event))
        .then(() =>
          {
            toaster.markToastsAsDoNotDismissOnLocationChange();
            const siteUrls = siteUrlsSelector(appStore.getState());
            Location.instance.setUrl(siteUrls.projectUrl($scope.workbook.project.id));
          });
    };

    $scope.changeDescription = (newDescription: string) => {
      return this.ChangeWorkbookDescriptionAction.execute($scope.workbook, newDescription)
        .then(dialogHelper.inlineEditResultHandler);
    };

    $scope.changeOwner = ($event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.changeOwnerOnWorkbooks($scope.workbookSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.moveToProject = ($event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.moveWorkbooksToProject($scope.workbookSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showRevisionHistory = () => {
      return this.RevisionHistoryAction.showWorkbookRevisionHistory($scope.workbook)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.changeTags = (newTagsList: string[]) => {
      return this.ContentActions.setTagsOnWorkbook($scope.workbook, newTagsList)
      .then(ResultHelper.rejectResultOnError($q))
      .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    // we open a dialog for changing "show tabs" when using the details dialog
    $scope.displayTabs = (newVal: boolean, $event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.setDisplayTabsOnWorkbook($scope.workbookSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.getProjectUrl = (projectId: string): string => {
      const siteUrls = siteUrlsSelector(appStore.getState());
      return siteUrls.projectUrl(projectId);
    };

    $scope.getUserUrl = (user: IUserInfo): string => {
      const siteUrls = siteUrlsSelector(appStore.getState());
      return siteUrls.userUrl(user.username, user.domainName);
    };
  }
}

Module.instance().controller('WorkbookDetailsDialogCtrl', WorkbookDetailsDialogCtrl);
